import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { HudButton } from '../hud';

const CATEGORIES = ['Sol', 'Mur', 'Plafond', 'Plinthe', 'Crédence', 'Faïence', 'Plan de travail', 'Sanitaire', 'Menuiserie', 'Façade meuble', 'Garde-corps', 'Terrasse', 'Autre'];
const PATTERNS = [['flat', 'Uni'], ['wood', 'Bois'], ['stone', 'Pierre'], ['tile', 'Carrelage'], ['fabric', 'Textile']];

const inputStyle = { background: 'var(--c-surface-1)', border: '1px solid var(--c-border)', color: 'var(--c-text)' };

/** AddMaterialDialog — saisie manuelle d'un matériau dans le job courant. */
export const AddMaterialDialog = ({ sections, roomTypes, onClose, onConfirm, busy }) => {
  const [form, setForm] = useState({ name: '', category: 'Sol', finish: '', ref: '', dimensions: '', section_id: sections?.[0]?.id || roomTypes?.[0]?.id || 'divers', thumb_color: '#8A7A66', thumb_pattern: 'flat' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const sectionOptions = [
    ...(sections || []).map((s) => ({ id: s.id, label: s.label })),
    ...(roomTypes || []).filter((rt) => !(sections || []).some((s) => s.id === rt.id)).map((rt) => ({ id: rt.id, label: rt.label })),
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/65 backdrop-blur-sm z-[1400]" onClick={busy ? undefined : onClose} />
      <div role="dialog" aria-modal="true" data-testid="materialtheque-add-dialog"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[520px] rounded-2xl z-[1410]"
        style={{ background: 'linear-gradient(to bottom, #0F0F12, #09090B)', border: '1px solid var(--c-border)', boxShadow: 'var(--shadow-cockpit)' }}>
        <div className="px-6 py-5 flex items-start justify-between" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Saisie manuelle</span>
            <h3 className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}>Ajouter un matériau</h3>
          </div>
          <button onClick={onClose} disabled={busy} className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0" data-testid="materialtheque-add-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2 block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Nom *</span>
            <input autoFocus value={form.name} onChange={set('name')} maxLength={160} placeholder="Ex : Parquet chêne clair lames larges" className="w-full px-3 py-2 rounded-lg text-[14px] outline-none" style={inputStyle} data-testid="materialtheque-add-name" />
          </label>
          <label className="block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Pièce / section</span>
            <select value={form.section_id} onChange={set('section_id')} className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none" style={inputStyle} data-testid="materialtheque-add-section">
              {sectionOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              <option value="divers">Divers</option>
            </select>
          </label>
          <label className="block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Catégorie</span>
            <select value={form.category} onChange={set('category')} className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none" style={inputStyle} data-testid="materialtheque-add-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Finition</span>
            <input value={form.finish} onChange={set('finish')} placeholder="Mat, satiné…" className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none" style={inputStyle} data-testid="materialtheque-add-finish" />
          </label>
          <label className="block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Référence</span>
            <input value={form.ref} onChange={set('ref')} placeholder="Fabricant / réf." className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none" style={inputStyle} data-testid="materialtheque-add-ref" />
          </label>
          <label className="block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Dimensions</span>
            <input value={form.dimensions} onChange={set('dimensions')} placeholder="600 × 600 mm" className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none" style={inputStyle} data-testid="materialtheque-add-dimensions" />
          </label>
          <div className="flex items-end gap-3">
            <label className="block">
              <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Couleur</span>
              <input type="color" value={form.thumb_color} onChange={set('thumb_color')} className="w-12 h-9 rounded-lg cursor-pointer" style={{ background: 'transparent', border: '1px solid var(--c-border)' }} data-testid="materialtheque-add-color" />
            </label>
            <label className="block flex-1">
              <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Motif</span>
              <select value={form.thumb_pattern} onChange={set('thumb_pattern')} className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none" style={inputStyle} data-testid="materialtheque-add-pattern">
                {PATTERNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--c-border)' }}>
          <HudButton variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="materialtheque-add-cancel">Annuler</HudButton>
          <HudButton variant="primary" size="md" onClick={() => onConfirm(form)} disabled={busy || !form.name.trim()} leftIcon={<Plus className="w-4 h-4" />} data-testid="materialtheque-add-confirm">
            {busy ? 'Ajout…' : 'Ajouter'}
          </HudButton>
        </div>
      </div>
    </>
  );
};

export default AddMaterialDialog;
